import { shallowEqual } from "react-redux";

// Common
import { DefaultLimits } from "constants/DefaultLimits";
import { useStrings } from "features/i18n/useStrings";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Feature slice
import { qualityFormActions } from "features/qualityForm/qualityFormSlice";
import { selectScorableQuestion } from "features/qualityForm/qualityFormSlices/qualityFormCommons";

// Feature common
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";

// Validations
import { hasMaximumCriticalAnswerOptions } from "features/qualityForm/qualityFormValidations";

// Question common
import { Property, PropertySection } from "../common/PropertySection";

// Strings
import strings from "./Question.strings";

export default function ScoringOptionsSection({
    questionId,
    isMultipleSelect,
}: {
    questionId: string;
    isMultipleSelect?: boolean;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);

    const { isCritical, isKill } = useAppSelector((state) => {
        // In the context of scoring option section, mcq is at the question level
        // but msq is at each multiple selection option.
        // Do not modify selectQuestion to take in isMultipleSelect and combine
        // them.
        const question = selectScorableQuestion(
            state,
            questionId,
            isMultipleSelect
        );
        return {
            isCritical: question?.isCritical,
            isKill: question?.isKill,
        };
    }, shallowEqual);

    const hasTooManyCriticalAnswerOptions = useAppSelector((state) =>
        hasMaximumCriticalAnswerOptions(state, questionId, isMultipleSelect)
    );

    const tooltipText = hasTooManyCriticalAnswerOptions
        ? t.labels.criticalQuestionAnswerLimitReached({
              criticalAnswerOptionCount:
                  DefaultLimits.maxCriticalAnswerOptionCount,
          })
        : undefined;

    const isMultipleSelectFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT);
    });
    let criticalLabel;
    let fatalLabel;
    if (isMultipleSelectFTEnabled) {
        criticalLabel = t.labels.scoring.critical;
        fatalLabel = t.labels.scoring.fatal;
    } else {
        criticalLabel = t.labels.scoring.criticalQuestion;
        fatalLabel = t.labels.scoring.fatalQuestion;
    }
    const scoringSectionTestId = isMultipleSelect
        ? "answer-scoring-section"
        : "scoring-section";

    return (
        <PropertySection
            title={t.labels.scoring.title}
            noBorderEnd={isMultipleSelect || false}
            horizontalDisplay={isMultipleSelect || false}
        >
            <div data-testid={scoringSectionTestId}>
                <Property
                    name="criticalQuestion"
                    checked={isCritical}
                    disabled={isKill || hasTooManyCriticalAnswerOptions}
                    onChange={() => {
                        dispatch(
                            qualityFormActions.changeCriticalQuestion({
                                questionId: questionId,
                                isMultipleSelect,
                            })
                        );
                    }}
                    label={criticalLabel}
                    tooltipText={tooltipText}
                />

                <Property
                    name="fatalQuestion"
                    checked={isKill}
                    disabled={!isCritical}
                    onChange={() => {
                        dispatch(
                            qualityFormActions.changeFatalQuestion({
                                questionId: questionId,
                                isMultipleSelect,
                            })
                        );
                    }}
                    label={fatalLabel}
                />
            </div>
        </PropertySection>
    );
}
